import React, { useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import SEOHead from './SEOHead';
import Footer from './Footer';
import styles from './Contact.module.css';
import { FiMapPin, FiPhone, FiMail, FiLinkedin, FiArrowRight, FiSend, FiUser } from "react-icons/fi";
import { API_URL } from '../config';

const initialState = {
  name: "",
  email: "",
  message: "",
};

export const Contact = () => {
  useEffect(() => {
    AOS.init({ duration: 900, once: true, offset: 60 });
  }, []);

  const [{ name, email, message }, setState] = useState(initialState);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/send-contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          email,
          message,
        }),
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        setSubmitted(true);
        setState(initialState);
        setTimeout(() => setSubmitted(false), 3000);
      } else {
        console.error("Form submission failed:", result);
        alert(result.error || "An error occurred. Please try again later.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const contactItems = [
    {
      icon: FiMapPin,
      label: "Address",
      content: "113, B2 Block, Karayampalayam Road, Mylampatti, Chinniyampalayam, Coimbatore, Tamil Nadu – 641 062",
    },
    {
      icon: FiPhone,
      label: "Phone",
      content: "+91-8925501851 (Preferred)\n+0422-714-1405",
    },
    {
      icon: FiMail,
      label: "Email",
      content: "info@ak-tech.in",
      href: "mailto:info@ak-tech.in",
    },
    {
      icon: FiLinkedin,
      label: "LinkedIn",
      content: "AK Consultants",
      href: "https://www.linkedin.com/company/ak-tech-group/posts/?feedView=all",
      external: true,
    },
  ];

  return (
    <div className={styles.pageWrap}>
      <SEOHead
        title="Contact Us | AK Consultants"
        description="Get in touch with AK Consultants for innovative technology solutions. Contact our expert team for AI, GIS, biomedical engineering, and software development services."
      />

      {/* ── Hero band ── */}
      <div className={styles.heroBand}>
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent} data-aos="fade-up">
          <span className={styles.eyebrow}>Let's Talk</span>
          <h1 className={styles.heroHeading}>Get In Touch</h1>
          <p className={styles.heroSub}>
            Fill out the form and we'll get back to you as soon as possible.
          </p>
        </div>
      </div>

      {/* ── Main content ── */}
      <section className={styles.contactSection}>
        <div className={styles.shapeTL} aria-hidden="true" />
        <div className={styles.shapeBR} aria-hidden="true" />
        {/* Visible circles — top-left cluster (3 circles) */}
        <div className={styles.circleA} aria-hidden="true" />
        <div className={styles.circleB} aria-hidden="true" />
        <div className={styles.circleE} aria-hidden="true" />
        {/* Visible circles — bottom-right cluster (3 circles) */}
        <div className={styles.circleC} aria-hidden="true" />
        <div className={styles.circleD} aria-hidden="true" />
        <div className={styles.circleF} aria-hidden="true" />
        <div className={styles.container}>
          <div className={styles.grid}>

            {/* ── Form card ── */}
            <div className={styles.formCard} data-aos="fade-up" data-aos-delay="0">
              <h2 className={styles.formTitle}>Send a Message</h2>
              <p className={styles.formSub}>We read every message and respond within 24 hours.</p>

              <form onSubmit={handleSubmit} name="contact-form" className={styles.form}>
                <div className={styles.fieldRow}>
                  <div className={styles.fieldGroup}>
                    <label className={styles.label} htmlFor="name">Name</label>
                    <div className={styles.inputWrap}>
                      <FiUser className={styles.inputIcon} size={16} />
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className={styles.input}
                        placeholder="Your name"
                        required
                        value={name}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.label} htmlFor="email">Email</label>
                    <div className={styles.inputWrap}>
                      <FiMail className={styles.inputIcon} size={16} />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className={styles.input}
                        placeholder="your@email.com"
                        required
                        value={email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                <div className={styles.fieldGroup}>
                  <label className={styles.label} htmlFor="message">Message</label>
                  <textarea
                    name="message"
                    id="message"
                    className={styles.textarea}
                    placeholder="Tell us about your project or inquiry..."
                    required
                    value={message}
                    onChange={handleChange}
                  />
                </div>

                <button type="submit" className={styles.submitBtn} disabled={loading}>
                  {loading ? (
                    <span className={styles.btnInner}>Sending…</span>
                  ) : (
                    <span className={styles.btnInner}>
                      Send Message <FiSend size={16} />
                    </span>
                  )}
                </button>
              </form>
            </div>

            {/* ── Info card ── */}
            <div className={styles.infoCard} data-aos="fade-up" data-aos-delay="120">
              <div className={styles.infoHeader}>
                <span className={styles.infoTag}>Contact Info</span>
                <h3 className={styles.infoTitle}>Every great project starts with a conversation.</h3>
                <p className={styles.infoSub}>Reach us through any of the channels below.</p>
              </div>

              <div className={styles.infoItems}>
                {contactItems.map((item, i) => (
                  <div key={i} className={styles.infoItem}>
                    <div className={styles.infoIconWrap}>
                      <item.icon size={18} />
                    </div>
                    <div className={styles.infoText}>
                      <span className={styles.infoLabel}>{item.label}</span>
                      {item.href ? (
                        <a
                          href={item.href}
                          className={styles.infoValue}
                          target={item.external ? "_blank" : undefined}
                          rel={item.external ? "noopener noreferrer" : undefined}
                        >
                          {item.content} {item.external && <FiArrowRight size={12} style={{ marginLeft: 4 }} />}
                        </a>
                      ) : (
                        <span className={styles.infoValue}>{item.content}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Decorative accent */}
              <div className={styles.infoAccent} aria-hidden="true" />
            </div>

          </div>
        </div>
      </section>

      {/* ── Success popup ── */}
      {submitted && (
        <div className={styles.popup}>
          <div className={styles.popupCard}>
            <div className={styles.popupIcon}>✓</div>
            <p className={styles.popupText}>Message sent successfully!</p>
          </div>
        </div>
      )}

      <div style={{ marginTop: '-80px' }}>
        <Footer />
      </div>
    </div>
  );
};

export default Contact;
